import Minze, { MinzeElement, Reactive, EventListeners, Attrs } from 'minze'
import { config, MODALS } from '../../constants'
import MainModule from '../../modules/main'
import { inlineStyle } from '../../modules/utils'
import { encodeSlides, LvcPrimarySliderItem } from '../../modules/sliderUtils'
import {
  AbstractSearchCore,
  MultipleResults,
  ISearchConfig,
  IFacet,
  IFacetOption,
  AbstractSubscriber,
} from '../../modules/search'
import { mapEvent } from '../../modules/utils'
import { Font } from '../../constants'
import { Locale } from '../../modules/locale'
import { getSlides } from './functions'

Locale.getInstance().registerKeySet('fr', 'nearMeSlider', {
  title: 'Autour de moi',
})

Locale.getInstance().registerKeySet('en', 'nearMeSlider', {
  title: 'Around me',
})

export interface LvcNearMeSlider {
  items: any[]
  total: number
  layout?: 'grid' | 'slider'
  disableTitle: boolean
}

export class NearMeSliderSubscriber extends AbstractSubscriber {
  private readonly index: string = 'eventsNearMe'
  private _core: AbstractSearchCore | undefined
  private _initialized = false
  private searchConfig: ISearchConfig = {}
  private facets = config.slidersConfigs.nearMe.facets
  private now = new Date()
  private nowUnix = Math.floor(this.now.getTime() / 1000)
  private unixOffset = config.slidersConfigs.nearMe.maxDaysToFilter * 24 * 60 * 60
  private dateFilters = [this.nowUnix, this.nowUnix + this.unixOffset]

  constructor() {
    super('eventsNearMe')
    let mainModule = MainModule.getInstance()

    mainModule.getSearchManager().subscribe(this)
  }

  async attached(searchCore: AbstractSearchCore): Promise<void> {
    this._core = searchCore
    this._initialized = true
  }

  detaching(): void {}

  async onSearchConfigUpdate(config: ISearchConfig): Promise<void> {}

  async onSearchTextUpdate(text: string): Promise<void> {}

  async onFacetUpdate(facet: IFacet[]): Promise<void> {}

  async onSortUpdate(sort: string): Promise<void> {}

  async onPaginationUpdate(page: number): Promise<void> {}

  async onResultUpdate(results: MultipleResults): Promise<void> {}

  async whenInitialized(): Promise<void> {
    if (!this._initialized) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(this.whenInitialized())
        }, 100)
      })
    }
  }

  async goToSearch() {
    this._core?.applySearchConfig(this.searchConfig)
    this._core?.goToSearch()
  }

  public async applyFacetsAndExecuteSearch(location: any): Promise<{ items: any; total: number }> {
    await this.whenInitialized()
    let indexesWithGeo = this._core?.getIndexesWithGeo() ?? []
    let options = this.searchConfig.options ?? {}
    let aroundLatLng = options.aroundLatLng ?? {}
    this.searchConfig = {
      ...this.searchConfig,
      facets: this.facets,
      hitsPerPage: config.slidersConfigs.nearMe.hitsPerPage,
    }
    if (location) {
      indexesWithGeo.forEach(index => {
        aroundLatLng[index] = `${location.latitude},${location.longitude}`
      })
      this.searchConfig.options = options
      this.searchConfig.options.aroundLatLng = aroundLatLng
      this.searchConfig.dateFilters = this.dateFilters
    }
    let response: MultipleResults = (await this._core?.executeSearch(
      this.searchConfig
    )) as MultipleResults

    return {
      items: response?.events_search?.items || [],
      total: response?.events_search?.total || 0,
    }
  }
}

//////////////////////////////////////////////////////////////////////////////////////

export class LvcNearMeSlider extends MinzeElement {
  attrs: Attrs = [
    ['layout', 'grid'],
    ['disable-title', true],
  ]
  reactive: Reactive = ['items', 'total']
  private subscriber: NearMeSliderSubscriber = new NearMeSliderSubscriber()
  isMobile = window.matchMedia('(max-width: 730px)').matches

  onReady = () => {
    let mainModule = MainModule.getInstance()
    mainModule.getEE().on('geo:location', location => {
      this.handleLocationChange(location)
    })
    const geo = mainModule.getGeo()
    if (geo) {
      this.handleLocationChange(geo.currentLocation)
    }
  }

  handleLocationChange = async (location: any) => {
    if (!location) {
      return
    }

    let items: LvcPrimarySliderItem[] = []
    const eventsResult = await this.subscriber.applyFacetsAndExecuteSearch(location)
    this.total = eventsResult.total
    let events = eventsResult.items
    if (events) {
      items = events.map((event: any) => mapEvent(event))
      this.items = items
    } else {
      this.items = []
    }
    this.rerender(true)
    Minze.cast('minze:lvc-near-me-slider-ready', {
      title: Locale.getKey('nearMeSlider.title'),
      tagName: 'lvc-near-me-slider',
      total: this.total,
    })
  }

  viewMore = async () => {
    await this.subscriber.goToSearch()
  }

  html = () => {
    if (!this.items || (this.items as any[]).length === 0) {
      return ``
    }
    return /*html*/ `
      <lvc-grid-layout data-component-location="activity_pack" data="${encodeSlides(this.items.filter(item => item).slice(0, 14))}"></lvc-grid-layout>
      <div class="view-more-layout">
        <div id="view-more">
          <div class="view-more-text">${Locale.getKey('lvc-activity-pack.viewMore', { title: Locale.getKey('nearMeSlider.title') })}</div>
          <div class="view-more-icon-wrapper">
            <lvc-arrow-right-icon></lvc-arrow-right-icon>
          </div>
        </div>
      </div>
      <lvc-slider-title text="${Locale.getKey('nearMeSlider.title')}" counter="${this.total}"></lvc-slider-title>
      <lvc-primary-slider
        data-component-location="activity_pack"
        list-name="${Locale.getKey('nearMeSlider.title')}"
        items-per-slide="3"
        fade-effect="true"
        data-slides="${encodeSlides(getSlides(this.items, this.total, 'lvc:near-me-slider-see-more'))}"
        style="margin-bottom: 1rem;"
        ></lvc-primary-slider> 
  `
  }
  css = () => `
      :host {
        ${!this.items || (this.items as any[]).length === 0 ? 'display: none;' : ''}
      }
      /* @media screen and (min-width: 600px) {
        lvc-primary-slider {
          height: 450px;
        }
      } */
      lvc-grid-layout {
        display: ${!this.layout || this.layout === 'grid' ? 'grid' : 'none'};
      }
      lvc-primary-slider {
        display: ${this.layout === 'slider' ? 'block' : 'none'};
      }
      .view-more-layout {
        display: ${this.layout === 'grid' ? 'flex' : 'none'};
        margin: auto;
        justify-content: center;
        margin-top: 60px;
      }
      #view-more {
        font-size: 1.5rem;
        font-weight: 500;
        color: #1E007D;
        text-transform: uppercase;
        display: flex;
        align-items: center;
        gap: 20px;
        cursor: pointer;
      }
      .view-more-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        background-color: #ffffff;
        border-radius: 100%;
      }
      lvc-arrow-right-icon {
        width: 35px;
        height: 35px;
        transform: rotate(-25deg);
      }
      .view-more-text {
        border-bottom: 1px solid #1E007D;
      }
      .title-container {
        display: ${!this.layout || this.layout === 'grid' ? 'none' : 'flex'};
        align-items: center;
        column-gap: 10px;
        cursor: pointer;
      }
      @media only screen and (max-width: 991px) {
        lvc-grid-layout {
          display: none;
        }
        lvc-primary-slider {
          display: block;
        }
        .title-container {
          display: flex;
        }
        .view-more-layout {
          display: none;
        }
      }
      .counter {
        display: inline-flex;
        height: 34px;
        min-width: 34px;
        padding: 7px;
        justify-content: center;
        align-items: center;
        border-radius: 40px;
        background: #1E007D;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        color: #FF4600;
        position: relative;
        top: 2px;
      }
    
  `

  eventListeners: EventListeners = [
    [window, 'lvc:near-me-slider-see-more', this.viewMore],
    ['.title-container', 'click', this.viewMore],
    ['#view-more', 'click', this.viewMore],
  ]
}
